import React, {
  useEffect, useState, useRef,
} from 'react';
import { ParsedUrlQuery } from 'querystring';

const DEFAULT_PREVIEW_LENGTH = 100;

interface UseTitleToggleProps {
  fullTitle: React.ReactNode;
  exportTitle: string;
  previewTitle?: React.ReactNode;
  hasMinimumCriteria?: boolean;
  query?: ParsedUrlQuery;
}

export function useTitleToggle({
  fullTitle,
  exportTitle,
  previewTitle,
  hasMinimumCriteria = true,
  query,
}: UseTitleToggleProps) {
  const [showFullTitle, setShowFullTitle] = useState(false);
  const [titleNeedsToggle, setTitleNeedsToggle] = useState(false);
  const [userHasToggledTitle, setUserHasToggledTitle] = useState(false);
  const titleRef = useRef<HTMLDivElement>(null);

  // Check if title needs toggle button based on content length
  useEffect(() => {
    if (fullTitle && hasMinimumCriteria && exportTitle) {
      // Use a simple length-based heuristic to determine if toggle is needed
      const titleLength = exportTitle.length;
      const needsToggle = titleLength > DEFAULT_PREVIEW_LENGTH;

      setTitleNeedsToggle(needsToggle);

      // Only automatically set showFullTitle if user hasn't manually toggled
      if (!userHasToggledTitle) {
        setShowFullTitle(!needsToggle);
      }
    }
  }, [fullTitle, hasMinimumCriteria, exportTitle, userHasToggledTitle]);

  // Reset user toggle state when query changes significantly
  useEffect(() => {
    if (query) {
      setUserHasToggledTitle(false);
    }
  }, [query]);

  const toggleTitle = () => {
    setShowFullTitle(!showFullTitle);
    setUserHasToggledTitle(true);
  };

  const displayTitle = showFullTitle ? fullTitle : (previewTitle || fullTitle);

  return {
    showFullTitle,
    titleNeedsToggle,
    displayTitle,
    toggleTitle,
    titleRef,
  };
}
